/**
 * Pure logic for the broker-mapping availability UX.
 *
 * Kept framework-free so the fail-closed decisions can be unit-tested in
 * isolation. This module intentionally knows nothing about *why* a broker
 * is unavailable (proxy family, IPv4/IPv6, capacity, cooling, etc.) — it
 * only consumes `{ broker_name, mapping_available }` from
 * GET /broker/mapping-availability and never re-derives or leaks internal
 * infrastructure policy to the UI.
 */

export const LOADING_MESSAGE = 'Checking broker availability…';
export const GENERIC_UNAVAILABLE_MESSAGE =
  'Broker selection is temporarily unavailable. Please contact Customer Support.';
export const MAP_REJECTED_MESSAGE =
  'This broker is currently unavailable for mapping. Please contact Customer Support.';

/**
 * Build a { [BROKER_NAME_UPPER]: boolean } selectability map for the given
 * list of supported broker names.
 *
 * Fail-closed rules:
 *  - while loading            -> every broker is false
 *  - on a fetch error         -> every broker is false
 *  - not an array response    -> every broker is false
 *  - broker missing from the
 *    availability response    -> that broker is false
 *
 * Matching is by canonical broker_name only (case-insensitive); an unknown
 * broker_name present in the response is ignored rather than turned into a
 * new option.
 */
export function computeBrokerAvailabilityMap({ brokerNames, availability, isLoading, isError }) {
  const names = (brokerNames || []).map((n) => String(n).toUpperCase());
  const result = {};
  for (const name of names) result[name] = false;

  if (isLoading || isError || !Array.isArray(availability)) {
    return result;
  }

  const byName = new Map(
    availability
      .filter((entry) => entry && entry.broker_name != null)
      .map((entry) => [String(entry.broker_name).toUpperCase(), Boolean(entry.mapping_available)]),
  );

  for (const name of names) {
    result[name] = byName.has(name) ? byName.get(name) : false;
  }
  return result;
}

export function isBrokerSelectable(availabilityMap, brokerName) {
  if (!brokerName) return false;
  return Boolean(availabilityMap?.[String(brokerName).toUpperCase()]);
}

// Internal infrastructure terms that must never reach user-facing text.
// Matches whole words/short tokens case-insensitively.
const INTERNAL_TERMS_PATTERN =
  /\b(proxy|proxies|ipv4|ipv6|inventory|cooling|allocation|allocator|address\s*pool|capacity|operator)\b/i;

/**
 * Turn a broker-map API error into approved, non-leaking user-facing text.
 *
 * - HTTP 503 (map-time race / route unavailable) always becomes the
 *   approved map-rejection message.
 * - Any other message containing internal infrastructure terminology
 *   (proxy/IPv4/IPv6/inventory/cooling/allocation/capacity/operator, etc.)
 *   is replaced with the generic Customer Support message.
 * - Otherwise safe, ordinary backend text (credential validation, the
 *   "requires de-map first" conflict) is passed through unchanged.
 */
export function sanitizeBrokerMapErrorMessage(error) {
  const status = Number(error?.status);
  if (status === 503) return MAP_REJECTED_MESSAGE;

  const raw = typeof error?.message === 'string' ? error.message : '';
  if (!raw || INTERNAL_TERMS_PATTERN.test(raw)) {
    return GENERIC_UNAVAILABLE_MESSAGE;
  }
  return raw;
}
